import { intlFormat } from 'date-fns';
import { PageProps } from 'gatsby';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import * as React from 'react';
import { DropdownSocialShare } from '../components/DropdownSocialShare';
import ArticleBody from './components/ArticleBody';
import ArticleHeader from './components/ArticleHeader';
import ArticleRelated from './components/ArticleRelated';
import { getTagLinks } from './utils/getTagLinks';

interface IRelatedArticleCards {
  edges: {
    node: IArticleCard;
  }[];
}

interface DataProps {
  node: IArticleContent;
  relatedArticles: IRelatedArticleCards;
  backfillArticles: IRelatedArticleCards;
  allStorageBrazeCard: AllStorageBrazeCards;
  translations: Translations;
}

const locales = { en: 'en-us', es: 'es' };

const domain = process.env.GATSBY_SITE_URL;

const ArticleTemplate: React.FC<PageProps<DataProps>> = function ({
  data: { node, relatedArticles, backfillArticles, allStorageBrazeCard },
}) {
  const { t } = useTranslation();
  const { field_media, field_tags = [], relatedProducts = [] } = node.relationships;
  const tags = getTagLinks(field_tags);
  const body = node.body?.processed ?? '';
  const date = intlFormat(
    new Date(node.changed),
    { day: 'numeric', year: 'numeric', month: 'numeric' },
    { locale: locales[node.langcode] },
  );
  const mediaType: string = field_media?.internal.type || 'media__image';
  const absoluteUrl = `${domain}${node.path?.alias}`;
  let { edges: related } = relatedArticles;
  const { edges: backfill } = backfillArticles;
  if (related.length < 3) {
    related = [...related, ...backfill].slice(0, 3);
  }
  const hasRelatedProducts = relatedProducts?.length > 0;

  const bodyText = t(
    'I just read this on Purina.com and I thought you might be interested:\n {{ title }}, {{ absoluteUrl }}',
    { title: node.title, absoluteUrl },
  );

  const shareComponent = (
    <DropdownSocialShare
      label={t('Share this article')}
      shareUrl={absoluteUrl}
      shareTitle={node.title}
      emailSubjectLine={t('Thought you might want to check this out...')}
      bodyText={bodyText}
      classNames={{
        menu: 'pds-bg-surface pds-shadow-stronger',
      }}
      articleType={node?.relationships?.field_tags?.[0]?.name || '(not set)'}
    />
  );

  return (
    <>
      <ArticleHeader
        node={node}
        tags={tags}
        date={date}
        mediaType={mediaType}
        shareComponent={shareComponent}
      />
      <ArticleBody
        body={body}
        title={node.title}
        relatedProducts={relatedProducts}
        hasRelatedProducts={hasRelatedProducts}
        allStorageBrazeCard={allStorageBrazeCard}
        shareComponent={shareComponent}
      />
      <ArticleRelated related={related} />
    </>
  );
};

export default ArticleTemplate;
